import { useQuery } from 'react-query';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CircularProgress } from '@mui/material';

import { AdminDashboardApi } from '@app/domain/dashboard/api/AdminDashboardApi';

import { DashboardCard } from '../shared/DashboardCard';
import { DASHBOARD_COLOR } from '../shared/dashboardColors';

const fmt = (n: number) => n.toLocaleString('nl-NL');
const weekLabel = (iso: string) => format(parseISO(iso), 'd MMM', { locale: nl });
const monthLabel = (iso: string) => format(parseISO(iso), 'MMM', { locale: nl });

// admin-dashboard-opdracht.md §4.5 - "Drie losse grafieken. Aantallen, geen bedragen." §2's
// eigen regel: waar een bedrag hoort te staan maar niet lokaal beschikbaar is, toon je het
// aantal en een link naar Stripe - nooit een geschat bedrag.
export const DashboardMoneyBand: React.FC<{ from: string; to: string }> = ({ from, to }) => {
  const query = useQuery(['admin-dashboard-money', from, to], () => AdminDashboardApi.getMoney(from, to));

  if (query.isLoading) {
    return (
      <div className="flex h-[220px] items-center justify-center">
        <CircularProgress />
      </div>
    );
  }
  if (!query.data) {
    return null;
  }

  const { conversionPerWeek, churnPerMonth, payingBaseOverTime } = query.data;

  return (
    <div className="flex flex-col gap-3.5">
      <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-3">
        <DashboardCard title="Conversie proef → betaald" subtitle="Per week">
          {conversionPerWeek.length ? (
            <div className="h-[180px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={conversionPerWeek} margin={{ top: 8, right: 8, left: 0 }}>
                  <CartesianGrid stroke={DASHBOARD_COLOR.grid} vertical={false} />
                  <XAxis
                    dataKey="weekStart"
                    tickFormatter={weekLabel}
                    tickLine={false}
                    axisLine={false}
                    fontSize={10.5}
                    stroke={DASHBOARD_COLOR.muted}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    fontSize={10.5}
                    width={30}
                    tickFormatter={(v: number) => `${v}%`}
                  />
                  <RTooltip
                    cursor={{ stroke: DASHBOARD_COLOR.grid }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) {
                        return null;
                      }
                      const row = payload[0].payload as typeof conversionPerWeek[number];
                      return (
                        <div className="rounded-md bg-gray-900 px-2.5 py-1.5 text-[12px] text-white">
                          <b>{weekLabel(label as string)}</b>
                          <br />
                          Conversie: <b>{row.percent.toFixed(1)}%</b>
                        </div>
                      );
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="percent"
                    stroke={DASHBOARD_COLOR.cat1}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 3.5, fill: DASHBOARD_COLOR.cat1, stroke: '#fff', strokeWidth: 2 }}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="flex h-[180px] items-center justify-center text-gray-400">Nog geen uitgerijpt cohort.</p>
          )}
        </DashboardCard>

        <DashboardCard title="Opzeggingen" subtitle="Per maand">
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={churnPerMonth} margin={{ top: 8, right: 8 }}>
                <CartesianGrid stroke={DASHBOARD_COLOR.grid} vertical={false} />
                <XAxis
                  dataKey="monthStart"
                  tickFormatter={monthLabel}
                  tickLine={false}
                  axisLine={false}
                  fontSize={10.5}
                  stroke={DASHBOARD_COLOR.muted}
                />
                <YAxis tickLine={false} axisLine={false} allowDecimals={false} fontSize={10.5} width={26} />
                <RTooltip
                  cursor={false}
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) {
                      return null;
                    }
                    const row = payload[0].payload as typeof churnPerMonth[number];
                    return (
                      <div className="rounded-md bg-gray-900 px-2.5 py-1.5 text-[12px] text-white">
                        <b>{monthLabel(label as string)}</b>
                        {row.running && <span className="opacity-70"> (loopt nog)</span>}
                        <br />
                        Opzeggingen: <b>{fmt(row.value)}</b>
                      </div>
                    );
                  }}
                />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {churnPerMonth.map((r) => (
                    <Cell key={r.monthStart} fill={r.running ? DASHBOARD_COLOR.running : DASHBOARD_COLOR.cat3} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </DashboardCard>

        <DashboardCard title="Betalend bestand" subtitle="Actieve betalende accounts">
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={payingBaseOverTime} margin={{ top: 8, right: 8, left: 0 }}>
                <CartesianGrid stroke={DASHBOARD_COLOR.grid} vertical={false} />
                <XAxis
                  dataKey="weekStart"
                  tickFormatter={weekLabel}
                  tickLine={false}
                  axisLine={false}
                  fontSize={10.5}
                  stroke={DASHBOARD_COLOR.muted}
                />
                <YAxis tickLine={false} axisLine={false} allowDecimals={false} fontSize={10.5} width={34} />
                <RTooltip
                  cursor={{ stroke: DASHBOARD_COLOR.grid }}
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) {
                      return null;
                    }
                    const row = payload[0].payload as typeof payingBaseOverTime[number];
                    return (
                      <div className="rounded-md bg-gray-900 px-2.5 py-1.5 text-[12px] text-white">
                        <b>{weekLabel(label as string)}</b>
                        {row.running && <span className="opacity-70"> (loopt nog)</span>}
                        <br />
                        Betalend: <b>{fmt(row.value)}</b>
                      </div>
                    );
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={DASHBOARD_COLOR.cat4}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 3.5, fill: DASHBOARD_COLOR.cat4, stroke: '#fff', strokeWidth: 2 }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </DashboardCard>
      </div>

      <DashboardCard className="py-3">
        <p className="text-[11.5px] text-gray-400">
          Bedragen staan hier bewust niet: die leven in Stripe en dit dashboard heeft daar geen koppeling mee.{' '}
          <a href="https://dashboard.stripe.com" target="_blank" rel="noreferrer" className="text-[#2a78d6]">
            Open Stripe →
          </a>
        </p>
      </DashboardCard>
    </div>
  );
};
